import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@coin/shared/auth/data-access';
import { first, from, switchMap } from 'rxjs';

@Injectable()
export class ChatStore {
  private readonly authService = inject(AuthService);
  private readonly overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;

  private readonly _threadId = signal(crypto.randomUUID());
  readonly threadId = this._threadId.asReadonly();

  readonly isAuthenticated = toSignal(this.authService.isAuthenticated$);

  /** Sticky: once true, stays true — prevents copilot-chat destruction on token refresh flicker. */
  readonly everAuthenticated = toSignal(this.authService.isAuthenticated$.pipe(first(v => !!v)), {
    initialValue: false
  });

  readonly activeUser = toSignal(
    this.authService.isAuthenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated ? from(this.authService.getActiveUser()) : from(Promise.resolve(undefined))
      )
    )
  );
  readonly user = computed(() => {
    const isAuthenticated = this.isAuthenticated();
    const user = this.activeUser();

    if (isAuthenticated && user) {
      return {
        gid: user.gid,
        firstName: user.given_name || user.firstName,
        lastName: user.family_name || user.lastName
      };
    }

    return null;
  });

  private readonly _isOpen = signal(false);
  readonly isOpen = this._isOpen.asReadonly();
  private loadChatDialog: (() => Promise<ComponentPortal<unknown>>) | null = null;
  private chatDialogPortal: ComponentPortal<unknown> | null = null;
  private isChatDialogLoading = false;

  /** The dialog is loaded on the first toggle, so CopilotKit stays out of the startup bundle. */
  setChatDialogLoader(loadChatDialog: () => Promise<ComponentPortal<unknown>>): void {
    this.loadChatDialog = loadChatDialog;
  }

  setOpen(isOpen: boolean): void {
    this._isOpen.set(isOpen);
  }

  clearConversation(): void {
    this._threadId.set(crypto.randomUUID());
  }

  toggle(): void {
    if (this.chatDialogPortal) {
      this.toggleOverlay(this.chatDialogPortal);
      return;
    }
    if (!this.loadChatDialog) {
      console.error('No chat dialog loader was set');
      return;
    }
    // Further clicks while the chunk loads would close the chat right after it opens.
    if (this.isChatDialogLoading) return;

    this.isChatDialogLoading = true;
    this.loadChatDialog()
      .then(
        componentPortal => {
          this.chatDialogPortal = componentPortal;
          this.toggleOverlay(componentPortal);
        },
        // A failed load (e.g. chunk gone after a deploy) is retried on the next click.
        error => console.error('Loading the chat dialog failed', error)
      )
      .finally(() => (this.isChatDialogLoading = false));
  }

  private toggleOverlay(componentPortal: ComponentPortal<unknown>): void {
    const isOpen = this._isOpen();

    if (!this.overlayRef) {
      // First open: create overlay once and attach the component
      const positionStrategy = this.overlay.position().global().bottom('0.25rem').right('0.25rem').top('0.25rem');
      const config: OverlayConfig = {
        hasBackdrop: false,
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        panelClass: 'chatbot-overlay-panel'
      };
      this.overlayRef = this.overlay.create(config);
      this.overlayRef.attach(componentPortal);
    } else {
      // Subsequent toggles: hide/show without destroying the component so chat state is preserved
      this.overlayRef.overlayElement.style.display = isOpen ? 'none' : '';
    }

    this._isOpen.set(!isOpen);
  }
}
